/**
 * The shared right-side detail drawer for the Bexio workspace tables.
 *
 * Same primitive and same shape as the warning drilldown UI-3b introduced
 * (`components/ui/sheet`, Radix Dialog underneath), so Escape, the overlay click,
 * the focus trap and the labelled dialog role come from the design system rather
 * than being re-implemented per page. What this adds is the layout the two pages
 * share: a sticky header, a scrolling body, and an optional footer.
 *
 * Width follows the reference panels — roomier than the primitive's default
 * `sm:max-w-sm`, near-fullscreen on a phone.
 */
import React from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";

export default function BexioDetailDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  badge,
  footer,
  children,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md lg:max-w-lg"
      >
        <SheetHeader className="shrink-0 border-b border-border p-5 pr-12 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="truncate text-base">{title}</SheetTitle>
              {subtitle ? (
                <SheetDescription className="truncate">{subtitle}</SheetDescription>
              ) : null}
            </div>
            {badge ? <div className="shrink-0">{badge}</div> : null}
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">{children}</div>

        {footer ? (
          <div className="shrink-0 border-t border-border p-4">{footer}</div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/** A titled block of key/value pairs inside the drawer. */
export function DrawerSection({ title, children }) {
  return (
    <section className="rounded-lg border border-border p-4">
      {title ? (
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
      ) : null}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>
    </section>
  );
}

/**
 * One key/value row. Renders NOTHING when the value is absent — an empty cell in
 * a detail panel reads as "this is blank in Bexio" rather than "this field does
 * not apply here", and the two are different claims.
 */
export function DrawerField({ label, value, wide = false, mono = false }) {
  if (value == null || value === "") return null;
  return (
    <div className={wide ? "col-span-2 min-w-0" : "min-w-0"}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`mt-0.5 break-words text-sm text-foreground ${
          mono ? "tabular-nums" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
